import { Button, Modal } from 'antd'
import React, { useContext } from 'react';
import Reports from './Plot/Reports';
import { Link } from 'react-router-dom'
import styles from '../Style/ResultPanel.module.scss'
import { IPreprocessedAnalyticsPayload } from '../../../typings/chart';
import { MarkerItem } from '../../../typings/home';
import { IReport } from '../../../typings/api';
import AnalyticsPanel from './AnalyticsPanel';
import { DatasetRecord } from '../Store/dataTypes';
import { SubscriptionType, UserGroupPermissions, WaterdataGroup } from '../../../typings/userGroupPermissions';
import ValidationTab from './SateliteTab';
import { useIsDwf } from '../../../generic/hooks';
import { StoreContext } from '../../../store/store';

export function DatasetOfflineModal(props: {
    datasetIsOfflineWarningVisible: boolean,
    handleCloseDatasetIsOfflineWarning: (status: boolean) => void,
}) {
    return (
        <Modal
            visible={props.datasetIsOfflineWarningVisible}
            onOk={() => props.handleCloseDatasetIsOfflineWarning(false)}
            onCancel={() => props.handleCloseDatasetIsOfflineWarning(false)}
        >
            <div className={styles.modal}>
                <div>This dataset is not available online.</div>
                {'Please '}
                <Link to={'/contact'}> {'contact us'}</Link>
                {' for more information or data requests.'}
                <div></div>{' '}
            </div>
        </Modal>
    )
}

export const ReportsModal = (props: {
    isReportsVisible: boolean,
    setIsReportsVisible: (item: boolean) => void,
    links: IReport[],
    updating: boolean,
    error: string,
    onUpdatePreprocessedDataLink: (payload: IPreprocessedAnalyticsPayload) => void,
    activePoint?: MarkerItem,
}) => {
    const {
        isReportsVisible,
        setIsReportsVisible,
        links,
        updating,
        error,
        onUpdatePreprocessedDataLink,
        activePoint
    } = props

    return (
        <Modal
            visible={isReportsVisible}
            footer={[
                <Button key="close" onClick={() => setIsReportsVisible(false)}>
                    Close
                </Button>,
            ]}
            onCancel={() => setIsReportsVisible(false)}
        >
            <Reports
                links={links}
                updating={updating}
                error={error}
                onUpdate={onUpdatePreprocessedDataLink}
                coordinates={activePoint?.coordinates!}
                previewDatasetId={activePoint?.downloadPage[0]?.datasetId ?? ''}
            />
        </Modal>
    );
};

export const AnalyticsModal = (props: {
    isAnalyticsVisible: boolean,
    setIsAnalyticsVisible: Function,
    setDatasetHeadline: React.Dispatch<React.SetStateAction<string>>;
    activePoint?: MarkerItem,
    setDatasetIsOfflineWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
    onMeshControl: (value: string) => any;
    isTnw: boolean
    point?: MarkerItem
    gridCellSize: string,
    waterDepth: string,
    userGroupPermissions: UserGroupPermissions
    category: string | string[]
}) => {
    const {
        isAnalyticsVisible,
        setIsAnalyticsVisible,
        setDatasetHeadline,
        activePoint,
        isTnw,
        onMeshControl,
        setDatasetIsOfflineWarningVisible,
        gridCellSize,
        waterDepth,
        userGroupPermissions,
        category,
        point
    } = props
    const { user } = useContext(StoreContext)
    const datasetId = activePoint?.downloadPage.find((item) => item.radioKey === category as string)?.datasetId ?? ''
    const isDwf = useIsDwf(datasetId) && user.authenticated
    return (
        <Modal
            visible={isAnalyticsVisible}
            onCancel={() => setIsAnalyticsVisible(false)}
            style={{ paddingBottom: 0, maxHeight: '89.5vh', height: '100%' }}
            width={'96.5vw'}
            footer={[
                <div key="footerText" />,
            ]}
            className="analytics-modal"
            title={[
                <div
                    className={styles.analyticsModal}
                    key={'analytics-modal-title-container'}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ textAlign: 'left' }}>Analytics</div>
                    </div>
                    <div
                        className="analyticsHeadline"
                        key={'analytics-modal-title-headline'}
                    >
                        {!isTnw && !isDwf
                            && (!userGroupPermissions?.waterdataGroup
                                || userGroupPermissions?.waterdataGroup === WaterdataGroup.WD_Users)
                            && (!userGroupPermissions?.subscriptionType
                                || userGroupPermissions?.subscriptionType === SubscriptionType.DataOnly)
                            && (<div className="analyticsSubHeadline">
                                It is free to analyze the last one year of data. Request a{' '}
                                <span>subscription</span> to analyze the full period of
                                data.
                            </div>
                            )}
                    </div>
                </div>,
            ]}
        >
            <AnalyticsPanel
                category={category}
                downloadItems={activePoint?.downloadPage!}
                coordinates={activePoint?.coordinates!}
                previewDatasetId={datasetId}
                userGroupPermissions={userGroupPermissions}
                setDatasetIsOfflineWarningVisible={setDatasetIsOfflineWarningVisible}
                pointProps={
                    activePoint?.coordinates[0] +
                    '_' +
                    activePoint?.coordinates[1] +
                    '_' +
                    waterDepth +
                    '_' +
                    gridCellSize
                }
                pointName={activePoint ? activePoint.label : ''}
                gridCellSize={gridCellSize.toString()!}
                waterDepth={waterDepth.toString()!}
                setDatasetHeadline={setDatasetHeadline}
                onMeshControl={onMeshControl}
                point={point}
            />
        </Modal>
    );
};

export const ValidationModal = (props: {
    isValidationVisible: boolean,
    setIsValidationVisible: React.Dispatch<React.SetStateAction<boolean>>,
    datasetHeadline: string,
    activePointMetadata?: DatasetRecord,
    activePoint?: MarkerItem,
    activePlotValidation: string[],
    setDatasetHeadline: React.Dispatch<React.SetStateAction<string>>;
    setActivePlotValidation: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
    const {
        isValidationVisible,
        setIsValidationVisible,
        activePointMetadata,
        activePoint,
        activePlotValidation,
        setDatasetHeadline,
        setActivePlotValidation
    } = props

    return (
        <Modal
            visible={isValidationVisible}
            onCancel={() => setIsValidationVisible(false)}
            style={{ paddingBottom: 0, maxHeight: '89.5vh', height: '100%' }}
            width={'96.5vw'}
            footer={[
                <div key="footerText"></div>,
            ]}
            title={[
                <div
                    className={styles.validationModal}
                    key={'validation-modal-title-container'}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ textAlign: 'left' }}>Validation</div>
                    </div>
                </div>,
            ]}
        >
            <ValidationTab
                downloadItems={activePoint?.downloadPage!}
                coordinates={activePoint?.coordinates!}
                previewDatasetId={activePointMetadata?.metadata?.DatasetId ?? ''}
                userGroupPermissions={activePointMetadata?.userGroupPermissions!}
                pointName={activePoint?.label ?? ''}
                gridCellSize={activePointMetadata?.cellSize?.toString()!}
                waterDepth={activePointMetadata?.depth?.toString()!}
                setDatasetHeadline={setDatasetHeadline}
                activePlot={activePlotValidation}
                setActivePlot={setActivePlotValidation}
            />
        </Modal>
    );
};
